import { Modal, ModalOverlay } from '@chakra-ui/react'
import { AppointmentCancellationModal } from '@components'
import type moment from 'moment'
import React, { useState } from 'react'
import {
  type PickerValue,
  useCalendarEvent
} from '../../context/calendar-event.context'
import { BpEvent, EventType, NotificationType } from '../../models'
import { AddEditEventModal } from './add-edit-event-modal'
import { ClientPickerModal } from './client-picker-modal'
import {
  type CreateClientFormValues,
  CreateClientModal
} from './create-client-modal'
import { MissingClientInfoModal } from './missing-client-info-modal'
import { SendIntakeModal } from './send-intake-modal'
import { SendNotificationModal } from './send-notification-modal'
import { ViewEventModal } from './view-event-modal'

// This component manages all the event modals using the context
export const EventModalsManager: React.FC = () => {
  const [pendingCancellationMode, setPendingCancellationMode] =
    useState<any>(null)

  const {
    user,
    showClientPickerModal,
    setShowClientPickerModal,
    showCreateClientModal,
    setShowCreateClientModal,
    setCurrentPickerValue,
    showAddEditEventModal,
    setShowAddEditEventModal,
    showSendNotificationModal,
    showViewEventModal,
    setShowViewEventModal,
    showMissingClientInfoModal,
    showCancelEventModal,
    setShowCancelEventModal,
    handleClientPickerSubmit,
    handleAddEditEventSubmit,
    handleSwitchEvent,
    handleDeleteEvent,
    handleRecurringCancellation,
    handleEnrollClient,
    handleMissingClientInfoSubmit,
    handleSaveEvent,
    isOpen,
    onClose,
    currentEvent,
    handleIntakeDocumentsSent,
    setShowSendNotificationModal,
    setShowSendIntakeModal,
    showSendIntakeModal,
    notificationType,
    setNotificationType,
    isDeleteEventLoading
  } = useCalendarEvent()

  return (
    <>
      {/* Appointment Cancellation Modal for Recurring Events - No outer Modal wrapper needed */}
      <AppointmentCancellationModal
        isOpen={showCancelEventModal}
        onClose={() => {
          setShowCancelEventModal(false)
          onClose()
        }}
        onConfirm={data => {
          // Store the cancellation mode and show notification modal
          setPendingCancellationMode(data.cancellationMode)
          setShowCancelEventModal(false)
          setNotificationType(NotificationType.CANCEL)
          setShowSendNotificationModal(true)
        }}
        patientName={currentEvent?.patient?.firstName || 'your client'}
        isLoading={isDeleteEventLoading}
      />

      {/* All other modals use the shared Modal wrapper */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />

        {/* Client Picker Modal */}
        {showClientPickerModal && (
          <ClientPickerModal
            onSubmit={(value: PickerValue) => {
              handleClientPickerSubmit(value)
            }}
            onCreateClient={(value: PickerValue) => {
              setCurrentPickerValue(value)
              setShowClientPickerModal(false)
              setShowCreateClientModal(true)
            }}
          />
        )}

        {/* Create Client Modal */}
        {showCreateClientModal && (
          <CreateClientModal
            onClose={onClose}
            onSubmit={(values: CreateClientFormValues) => {
              handleEnrollClient(
                values.firstName,
                values.lastName,
                values.pronoun,
                values.email,
                values.phoneNumber
              )
            }}
          />
        )}

        {/* Add/Edit Event Modal */}
        {showAddEditEventModal && (
          <AddEditEventModal
            onSubmit={handleAddEditEventSubmit}
            onCancel={onClose}
            onEditClient={() => {
              setShowAddEditEventModal(false)
              setShowClientPickerModal(true)
            }}
            onSwitchEvent={(eventType: EventType) => {
              handleSwitchEvent(eventType)
            }}
          />
        )}

        {/* Send Notification Modal */}
        {showSendNotificationModal && (
          <SendNotificationModal
            onCancel={() => {
              setPendingCancellationMode(null)
              onClose()
            }}
            onSubmit={async (sendNotification?: boolean) => {
              if (notificationType === NotificationType.CANCEL) {
                if (pendingCancellationMode && currentEvent?.id) {
                  // This is a recurring cancellation, use the stored mode
                  const cancellationData = {
                    sendNotification: sendNotification || false,
                    cancellationMode: pendingCancellationMode
                  }
                  handleRecurringCancellation(cancellationData)
                  setPendingCancellationMode(null)
                } else {
                  // Regular single event deletion
                  handleDeleteEvent(currentEvent?.id, sendNotification)
                }
                onClose()
                return
              } else {
                await handleSaveEvent(currentEvent, sendNotification)
                // Check if this is a new client
                const isNewClient = currentEvent?.isNewClient || false
                if (isNewClient) {
                  setShowSendNotificationModal(false)
                  setShowSendIntakeModal(true)
                } else {
                  onClose()
                }
              }
            }}
          />
        )}

        {/* Send Intake Modal */}
        {showSendIntakeModal && currentEvent?.patient?.id && (
          <SendIntakeModal
            patientId={currentEvent?.patient?.id}
            clinicId={user.clinic.id}
            onClose={onClose}
            onSendDocuments={() => {
              handleIntakeDocumentsSent()
              onClose()
            }}
            onNotNow={() => {
              onClose()
            }}
          />
        )}

        {/* View Event Modal */}
        {showViewEventModal && (
          <ViewEventModal
            onEdit={() => {
              setShowViewEventModal(false)
              setShowAddEditEventModal(true)
            }}
            onClickDelete={() => {
              if (currentEvent?.eventType === EventType.CLIENT_SESSION) {
                // Check if this is a recurring event
                if (currentEvent?.recurrenceTemplate) {
                  setShowViewEventModal(false)
                  setShowCancelEventModal(true)
                } else {
                  setNotificationType(NotificationType.CANCEL)
                  setShowViewEventModal(false)
                  setShowSendNotificationModal(true)
                }
              } else {
                handleDeleteEvent(currentEvent?.id)
                onClose()
              }
            }}
            onClose={onClose}
          />
        )}

        {/* Missing Client Info Modal */}
        {showMissingClientInfoModal && (
          <MissingClientInfoModal
            onCancel={() => {
              onClose()
            }}
            onSendManually={() => {
              handleSaveEvent(currentEvent)
              onClose()
            }}
            onSubmit={handleMissingClientInfoSubmit}
          />
        )}
      </Modal>
    </>
  )
}

// Hook to use in parent components to control the modals
export const useCalendarModals = () => {
  const context = useCalendarEvent()

  return {
    // Methods to open modals

    openAddEventModal: (selectedDateTime?: moment.Moment) => {
      context.setIsEditMode(false)
      if (selectedDateTime) {
        context.setCurrentEvent(
          new BpEvent({
            clinician_id: context.user.id,
            start: selectedDateTime.toISOString()
          })
        )
      } else {
        context.setCurrentEvent(new BpEvent({ clinician_id: context.user.id }))
      }
      context.setIsOpen(true)
      context.setShowClientPickerModal(true)
    },

    openEditEventModal: (event: any) => {
      context.setCurrentEvent(event)
      context.setIsEditMode(true)
      context.setIsOpen(true)
      context.setShowAddEditEventModal(true)
    },

    openViewEventModal: (event: any) => {
      context.setCurrentEvent(event)
      context.setIsOpen(true)
      context.setShowViewEventModal(true)
    },

    // Clear all modals and state
    clearAllModals: context.clearEvent
  }
}
